import { SelectionInfo } from '../types/wordTypes';
import { DocumentReader } from './DocumentReader';
import { log, logError } from '../../utils/logging';

/* global Word, Office */

export type SelectionChangeCallback = (selection: SelectionInfo) => void;

export class SelectionTracker {
  private callbacks: SelectionChangeCallback[] = [];
  private isTracking = false;

  private isSelectionHandlerAvailable(): boolean {
    return (
      typeof Office !== 'undefined' &&
      !!Office.context?.document &&
      typeof Office.context.document.addHandlerAsync === 'function' &&
      !!Office.EventType?.DocumentSelectionChanged
    );
  }

  /**
   * Starts tracking selection changes
   */
  startTracking(): void {
    if (this.isTracking) {
      return;
    }

    if (!this.isSelectionHandlerAvailable()) {
      log('Selection tracking unavailable outside a Word document host');
      return;
    }

    this.isTracking = true;
    this.setupSelectionHandler();
    log('Selection tracking started');
  }

  /**
   * Stops tracking selection changes
   */
  stopTracking(): void {
    this.isTracking = false;
    this.callbacks = [];
    log('Selection tracking stopped');
  }

  /**
   * Registers a callback for selection changes
   */
  onSelectionChange(callback: SelectionChangeCallback): () => void {
    this.callbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Gets the current selection immediately (or paragraph if no text selected)
   */
  async getCurrentSelection(): Promise<SelectionInfo> {
    return await DocumentReader.getSelectionOrParagraph();
  }

  private setupSelectionHandler(): void {
    if (!this.isSelectionHandlerAvailable()) {
      log('Selection handler setup skipped because Office document API is unavailable');
      return;
    }

    // Use Office.js event handler for selection changes
    // @ts-ignore - Office.context is available at runtime
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      this.handleSelectionChange.bind(this),
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          logError('Failed to setup selection handler', result.error);
        } else {
          log('Selection handler setup complete');
        }
      }
    );
  }

  private async handleSelectionChange(): Promise<void> {
    try {
      // Use getSelectionOrParagraph to support click-to-select paragraph
      const selection = await DocumentReader.getSelectionOrParagraph();
      
      // Notify all callbacks
      this.callbacks.forEach((callback) => {
        try {
          callback(selection);
        } catch (error) {
          logError('Error in selection change callback', error);
        }
      });
    } catch (error) {
      logError('Error handling selection change', error);
    }
  }
}

